import React, { useEffect, useRef, useState, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";
import * as turf from "@turf/turf";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "next-themes";
import { mapStyleFor } from "@/lib/mapStyle";
import { RouteSvgOverlay } from "@/components/RouteSvgOverlay";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { fmtDistance, fmtLivePace } from "@/lib/units";
import { computeElevation } from "@/lib/elevation";
import { expandSegments, isPushEffort, fmtClock } from "@/lib/runSegments";
import { toast } from "sonner";
import { Play, Pause, SkipForward, Square, Loader2, X, Navigation } from "lucide-react";

const M_TO_FT = 3.28084;

export default function GuidedRun() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { resolvedTheme } = useTheme();

  const [workout, setWorkout] = useState(null);
  const [timeline, setTimeline] = useState([]);
  const boundsRef = useRef([]); // cumulative end-seconds per phase
  const totalRef = useRef(0);

  // Engine state
  const [started, setStarted] = useState(false);
  const [paused, setPaused] = useState(false);
  const [finishing, setFinishing] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [phaseIdx, setPhaseIdx] = useState(0);

  // Wall-clock timing refs (robust to screen-off / backgrounding)
  const startTs = useRef(null);
  const pausedMs = useRef(0);
  const pauseStartTs = useRef(null);
  const skipOffset = useRef(0);
  const prevIdx = useRef(-1);
  const finishedRef = useRef(false);
  const audioCtx = useRef(null);

  // GPS refs
  const container = useRef(null);
  const map = useRef(null);
  const [mapInstance, setMapInstance] = useState(null);
  const watchId = useRef(null);
  const coordsRef = useRef([]);
  const [coords, setCoords] = useState([]);
  const [distance, setDistance] = useState(0);
  const beacon = useRef(null);
  const wakeLock = useRef(null);
  const elevationRef = useRef(0);
  const lastAlt = useRef(null);
  const onPositionRef = useRef(null);

  // ---- Load workout + expand timeline ----
  useEffect(() => {
    api.get(`/run-workouts/${id}`).then(({ data }) => {
      setWorkout(data);
      const flat = expandSegments(data.segments);
      setTimeline(flat);
      const b = []; let acc = 0;
      flat.forEach((p) => { acc += p.duration_sec; b.push(acc); });
      boundsRef.current = b;
      totalRef.current = acc;
    }).catch(() => { toast.error("Could not load workout"); navigate(`/run-workouts/${id}`); });
  }, [id, navigate]);

  const computeElapsed = useCallback(() => {
    if (startTs.current == null) return 0;
    const pausedSoFar = pausedMs.current + (pauseStartTs.current != null ? Date.now() - pauseStartTs.current : 0);
    return Math.max(0, Math.floor((Date.now() - startTs.current - pausedSoFar) / 1000) + skipOffset.current);
  }, []);

  // ---- Audio + haptic cues ----
  const beep = (freq, dur, when) => {
    try {
      const ctx = audioCtx.current;
      if (!ctx) return;
      const o = ctx.createOscillator();
      const g = ctx.createGain();
      o.connect(g); g.connect(ctx.destination);
      o.frequency.value = freq;
      const t = ctx.currentTime + (when || 0);
      o.start(t);
      g.gain.setValueAtTime(0.25, t);
      g.gain.exponentialRampToValueAtTime(0.001, t + dur);
      o.stop(t + dur);
    } catch (e) { /* audio best-effort */ }
  };
  const vibrate = (pat) => { try { if (navigator.vibrate) navigator.vibrate(pat); } catch (e) {} };
  const cue = (kind) => {
    if (kind === "push") { beep(988, 0.16, 0); beep(1245, 0.18, 0.18); vibrate([140, 60, 160]); }
    else if (kind === "ease") { beep(660, 0.2, 0); vibrate(120); }
    else if (kind === "finish") { beep(880, 0.2, 0); beep(1108, 0.2, 0.22); beep(1318, 0.34, 0.46); vibrate([220, 90, 220, 90, 320]); }
  };

  // ---- GPS ----
  const showBeacon = useCallback((lngLat) => {
    if (!map.current) return;
    if (!beacon.current) {
      const el = document.createElement("div");
      el.className = "run-beacon";
      el.setAttribute("data-testid", "guided-current-beacon");
      const pulse = document.createElement("span");
      pulse.className = "run-beacon-pulse";
      el.appendChild(pulse);
      beacon.current = new maplibregl.Marker({ element: el }).setLngLat(lngLat).addTo(map.current);
    } else {
      beacon.current.setLngLat(lngLat);
    }
  }, []);

  const onPosition = useCallback((pos) => {
    const { longitude, latitude, altitude, accuracy } = pos.coords;
    const nc = [longitude, latitude];
    const prev = coordsRef.current;
    showBeacon(nc);
    if (accuracy != null && accuracy > 120 && prev.length) return;
    if (prev.length) {
      const d = turf.distance(turf.point(prev[prev.length - 1]), turf.point(nc), { units: "meters" });
      if (d < 5) return; // filter jitter
    }
    const updated = [...prev, nc];
    coordsRef.current = updated;
    setCoords(updated);
    const line = updated.length > 1 ? turf.lineString(updated) : null;
    setDistance(line ? turf.length(line, { units: "miles" }) : 0);
    if (altitude != null) {
      if (lastAlt.current != null && altitude > lastAlt.current) elevationRef.current += (altitude - lastAlt.current);
      lastAlt.current = altitude;
    }
    if (map.current) {
      try { map.current.easeTo({ center: nc, zoom: 16, duration: 500 }); } catch (e) {}
    }
  }, [showBeacon]);
  useEffect(() => { onPositionRef.current = onPosition; }, [onPosition]);

  const startWatch = () => {
    if (!navigator.geolocation) return;
    watchId.current = navigator.geolocation.watchPosition(
      (pos) => onPositionRef.current && onPositionRef.current(pos),
      (err) => {
        if (err.code === 1) toast.error("Location denied — the timer still works, but we can't map your route.");
      },
      { enableHighAccuracy: true, maximumAge: 0, timeout: 15000 }
    );
  };
  const stopWatch = () => { if (watchId.current) { navigator.geolocation.clearWatch(watchId.current); watchId.current = null; } };
  const requestWakeLock = async () => { try { if ("wakeLock" in navigator) wakeLock.current = await navigator.wakeLock.request("screen"); } catch (e) {} };
  const releaseWakeLock = async () => { try { if (wakeLock.current) { await wakeLock.current.release(); wakeLock.current = null; } } catch (e) {} };

  // ---- Build the base map once the workout starts ----
  useEffect(() => {
    if (!started || map.current || !container.current) return;
    map.current = new maplibregl.Map({
      container: container.current,
      style: mapStyleFor(resolvedTheme),
      center: [-74.006, 40.7128],
      zoom: 15,
      attributionControl: { compact: true },
    });
    setMapInstance(map.current);
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (pos) => { const ll = [pos.coords.longitude, pos.coords.latitude]; if (map.current) map.current.flyTo({ center: ll, zoom: 16 }); showBeacon(ll); },
        () => {}, { enableHighAccuracy: true }
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started]);

  // Cleanup on unmount
  useEffect(() => () => { stopWatch(); releaseWakeLock(); if (map.current) { map.current.remove(); map.current = null; } }, []);

  // ---- The interval engine tick ----
  const finishRef = useRef(null);
  useEffect(() => {
    if (!started || paused || finishedRef.current) return undefined;
    const tick = () => {
      const e = computeElapsed();
      setElapsed(e);
      const bounds = boundsRef.current;
      let idx = bounds.findIndex((b) => e < b);
      if (idx === -1) { if (finishRef.current) finishRef.current(true); return; }
      setPhaseIdx(idx);
      if (idx !== prevIdx.current) {
        cue(isPushEffort(timeline[idx] && timeline[idx].effort) ? "push" : "ease");
        prevIdx.current = idx;
      }
    };
    const iv = setInterval(tick, 500);
    return () => clearInterval(iv);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [started, paused, timeline]);

  // Re-sync the clock + wake lock when returning to the tab mid-workout.
  useEffect(() => {
    const onVis = () => {
      if (document.visibilityState === "visible" && started && !paused && !finishedRef.current) {
        requestWakeLock();
        setElapsed(computeElapsed());
      }
    };
    document.addEventListener("visibilitychange", onVis);
    return () => document.removeEventListener("visibilitychange", onVis);
  }, [started, paused, computeElapsed]);

  // ---- Controls ----
  const start = () => {
    try {
      audioCtx.current = audioCtx.current || new (window.AudioContext || window.webkitAudioContext)();
      if (audioCtx.current.state === "suspended") audioCtx.current.resume();
    } catch (e) {}
    startTs.current = Date.now(); pausedMs.current = 0; pauseStartTs.current = null; skipOffset.current = 0;
    prevIdx.current = 0; finishedRef.current = false;
    coordsRef.current = []; setCoords([]); setDistance(0); elevationRef.current = 0; lastAlt.current = null;
    setStarted(true); setPaused(false); setElapsed(0); setPhaseIdx(0);
    cue(isPushEffort(timeline[0] && timeline[0].effort) ? "push" : "ease");
    startWatch(); requestWakeLock();
  };

  const pauseResume = () => {
    if (paused) {
      if (pauseStartTs.current != null) { pausedMs.current += Date.now() - pauseStartTs.current; pauseStartTs.current = null; }
      startWatch(); requestWakeLock(); setPaused(false);
    } else {
      pauseStartTs.current = Date.now();
      stopWatch(); releaseWakeLock(); setPaused(true);
    }
  };

  const skipPhase = () => {
    const bounds = boundsRef.current;
    const e = computeElapsed();
    const idx = bounds.findIndex((b) => e < b);
    if (idx === -1) return;
    skipOffset.current += (bounds[idx] - e);
    setElapsed(computeElapsed());
  };

  const saveAndExit = useCallback(async (auto) => {
    if (finishedRef.current && !auto) return;
    finishedRef.current = true;
    stopWatch(); releaseWakeLock();
    setPaused(false); setFinishing(true);
    if (auto) cue("finish");
    const finalDuration = Math.max(1, Math.min(computeElapsed(), totalRef.current || computeElapsed()));
    const route = coordsRef.current;
    // Nothing meaningful to save (indoor, very short, no GPS) — just celebrate + leave.
    if (route.length < 2 && finalDuration < 60) {
      toast.success("Guided workout complete!");
      navigate(`/run-workouts/${id}`);
      return;
    }
    const dist = distance;
    const pace = dist > 0 ? parseFloat(((finalDuration / 60) / dist).toFixed(2)) : 0;
    const payload = {
      distance_mi: parseFloat(dist.toFixed(2)),
      duration_sec: finalDuration,
      pace_min_per_mi: pace,
      avg_pace: pace,
      calories: Math.round(dist * 100),
      elevation_gain_ft: parseFloat((elevationRef.current * M_TO_FT).toFixed(0)),
      splits: [],
      route_geojson: { type: "LineString", coordinates: route },
      title: workout ? workout.workout_name : "Guided Run",
      run_workout_id: workout ? workout.id : id,
      run_workout_name: workout ? workout.workout_name : undefined,
      run_type: workout ? workout.run_type : undefined,
    };
    try {
      if (route.length >= 2) {
        try {
          const elev = await computeElevation(route, payload.distance_mi);
          if (elev) { payload.elevation_gain_ft = elev.gain_ft; payload.elevation_profile = elev.profile; }
        } catch (e) {}
      }
      const { data } = await api.post("/runs", payload);
      toast.success("Workout complete — run saved!");
      navigate(`/runs/${data.id}`);
    } catch (e) {
      toast.error("Couldn't save the run");
      navigate("/run-workouts");
    }
  }, [computeElapsed, distance, id, navigate, workout]);
  useEffect(() => { finishRef.current = saveAndExit; }, [saveAndExit]);

  const abandon = () => {
    stopWatch(); releaseWakeLock(); finishedRef.current = true;
    navigate(`/run-workouts/${id}`);
  };

  // ---- Derived display ----
  const cur = timeline[phaseIdx] || null;
  const nxt = timeline[phaseIdx + 1] || null;
  const bounds = boundsRef.current;
  const phaseEnd = bounds[phaseIdx] || 0;
  const phaseStart = phaseIdx > 0 ? bounds[phaseIdx - 1] : 0;
  const phaseRemaining = Math.max(0, phaseEnd - elapsed);
  const phaseLen = Math.max(1, phaseEnd - phaseStart);
  const phaseProgress = Math.min(100, ((elapsed - phaseStart) / phaseLen) * 100);
  const overallProgress = totalRef.current ? Math.min(100, (elapsed / totalRef.current) * 100) : 0;
  const push = cur ? isPushEffort(cur.effort) : false;

  // ================= PRE-START OVERVIEW =================
  if (!started) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <header className="sticky top-0 z-40 border-b border-border bg-background/80 backdrop-blur">
          <div className="mx-auto flex max-w-[560px] items-center justify-between px-4 h-14">
            <button onClick={() => navigate(`/run-workouts/${id}`)} className="flex items-center gap-2 text-muted-foreground hover:text-foreground" data-testid="guided-back">
              <X className="h-5 w-5" /> <span className="text-sm">Close</span>
            </button>
            <Logo className="h-8 w-8 rounded-lg" />
          </div>
        </header>
        <main className="mx-auto max-w-[560px] px-4 py-6 pb-28">
          {!workout ? (
            <p className="text-muted-foreground">Loading…</p>
          ) : (
            <div className="space-y-4" data-testid="guided-overview">
              <div>
                <p className="text-xs uppercase tracking-wide text-[#ccff0c] font-semibold">{workout.run_type} · {workout.difficulty}</p>
                <h1 className="font-display text-3xl font-bold leading-tight mt-1">{workout.workout_name}</h1>
                <p className="text-muted-foreground mt-1.5">{workout.workout_summary}</p>
              </div>
              <Card className="rounded-2xl bg-card border-border p-4">
                <div className="flex items-start gap-3">
                  <Navigation className="h-5 w-5 text-[#ccff0c] mt-0.5" />
                  <p className="text-sm text-muted-foreground">The guided timer will auto-advance each interval with a beep and vibration. We'll track your route, distance and pace by GPS, and save the run when you finish. Works on a treadmill too — just follow the timer.</p>
                </div>
              </Card>
              <Card className="rounded-2xl bg-card border-border p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-2">Timeline · {fmtClock(totalRef.current)} total</p>
                <div className="space-y-1.5 max-h-[38vh] overflow-y-auto rd-scrollbar pr-1">
                  {timeline.map((p, i) => (
                    <div key={i} className="flex items-center justify-between text-sm">
                      <span className="flex items-center gap-2">
                        <span className={`h-2 w-2 rounded-full ${isPushEffort(p.effort) ? "bg-[#ccff0c]" : "bg-muted-foreground/40"}`} />
                        <span className="font-medium">{p.label}</span>
                        {p.roundText && <span className="text-[11px] text-muted-foreground">({p.roundText})</span>}
                        <span className="text-[11px] text-muted-foreground">RPE {p.rpe}</span>
                      </span>
                      <span className="font-mono text-muted-foreground">{fmtClock(p.duration_sec)}</span>
                    </div>
                  ))}
                </div>
              </Card>
            </div>
          )}
        </main>
        {workout && (
          <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-background/90 backdrop-blur" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
            <div className="mx-auto max-w-[560px] px-4 py-3">
              <Button data-testid="guided-start-button" onClick={start} className="w-full h-14 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] rd-glow font-bold text-lg rounded-2xl">
                <Play className="h-6 w-6 mr-1.5" /> Start Workout
              </Button>
            </div>
          </div>
        )}
      </div>
    );
  }

  // ================= ACTIVE GUIDED RUN =================
  return (
    <div className="min-h-screen bg-background text-foreground">
      {/* Map */}
      <div className="relative">
        <div ref={container} data-testid="guided-map" style={{ height: "34vh", width: "100%" }} />
        <RouteSvgOverlay map={mapInstance} coordinates={coords} lineWidth={5} />
        <button onClick={abandon} data-testid="guided-abandon" className="absolute top-3 left-3 h-9 w-9 rounded-full bg-black/55 border border-border backdrop-blur flex items-center justify-center text-white/90 z-10">
          <X className="h-5 w-5" />
        </button>
        <div className="absolute top-3 right-3 grid grid-cols-2 gap-2 z-10">
          <div data-testid="guided-live-distance" className="rounded-xl bg-black/55 border border-border backdrop-blur px-3 py-1.5 text-center">
            <p className="font-mono text-base font-bold text-white">{fmtDistance(distance, user)}</p>
            <p className="text-[10px] text-white/70">Distance</p>
          </div>
          <div data-testid="guided-live-pace" className="rounded-xl bg-black/55 border border-border backdrop-blur px-3 py-1.5 text-center">
            <p className="font-mono text-base font-bold text-white">{fmtLivePace(distance, elapsed, user)}</p>
            <p className="text-[10px] text-white/70">Pace</p>
          </div>
        </div>
      </div>

      {/* Current phase hero */}
      <div className="mx-auto max-w-[560px] px-4 pt-4 pb-40">
        <Card
          data-testid="guided-phase-card"
          className={`rounded-3xl p-6 text-center transition-colors ${push ? "border-[#ccff0c]/50 bg-[#ccff0c]/10 rd-glow" : "border-border bg-card"}`}
        >
          <p data-testid="guided-phase-label" className={`font-display text-2xl font-bold uppercase tracking-wide ${push ? "text-[#ccff0c]" : "text-foreground"}`}>
            {cur ? cur.label : ""}
            {cur && cur.roundText ? <span className="text-base align-middle ml-2 text-muted-foreground">{cur.roundText}</span> : null}
          </p>
          <p className="text-sm text-muted-foreground mt-1">Effort · RPE {cur ? cur.rpe : "—"}</p>
          <p data-testid="guided-countdown" className="font-mono font-bold tabular-nums leading-none mt-4 text-7xl">{fmtClock(phaseRemaining)}</p>
          <Progress value={phaseProgress} className="h-2 mt-5" />
          <p className="text-xs text-muted-foreground mt-3">
            {nxt ? <>Next: <span className="text-foreground font-medium">{nxt.label}</span> · {fmtClock(nxt.duration_sec)}</> : "Final segment — finish strong!"}
          </p>
        </Card>

        {/* Overall progress */}
        <div className="mt-4">
          <div className="flex items-center justify-between text-xs text-muted-foreground mb-1.5">
            <span>Overall</span>
            <span className="font-mono">{fmtClock(elapsed)} / {fmtClock(totalRef.current)}</span>
          </div>
          <Progress data-testid="guided-overall-progress" value={overallProgress} className="h-2" />
        </div>
      </div>

      {/* Controls */}
      <div className="fixed bottom-0 left-0 right-0 z-40 border-t border-border bg-background/90 backdrop-blur" style={{ paddingBottom: "env(safe-area-inset-bottom)" }}>
        <div className="mx-auto max-w-[560px] px-4 py-3 flex items-center justify-center gap-3">
          <Button data-testid="guided-pause" onClick={pauseResume} className="h-13 px-5 py-3 bg-secondary border border-border text-foreground rounded-2xl">
            {paused ? <Play className="h-6 w-6" /> : <Pause className="h-6 w-6" />}
          </Button>
          <Button data-testid="guided-skip" onClick={skipPhase} className="h-13 px-5 py-3 bg-secondary border border-border text-foreground rounded-2xl">
            <SkipForward className="h-6 w-6" />
          </Button>
          <Button data-testid="guided-finish" onClick={() => saveAndExit(false)} disabled={finishing} className="h-13 px-8 py-3 bg-[#ccff0c] text-black hover:bg-[#d7ff3a] font-bold rounded-2xl">
            {finishing ? <Loader2 className="h-6 w-6 animate-spin" /> : <><Square className="h-5 w-5 mr-1.5" /> Finish</>}
          </Button>
        </div>
      </div>
    </div>
  );
}
